import React, { useEffect, useState, useCallback } from 'react';
import './UserTeacherAnalytics.css';
import { Box, Typography, Grid } from '@mui/material';
import AnalyticsCard, {
  readGrowth,
  SectionUnavailable,
  settledData,
} from '../AnalyticsCard/AnalyticsCard';
import UserTeacherLineChart from './UserTeacherLineChart/UserTeacherLineChart';
import DonutChart from '../DonutChart/DonutChart';
import TopPerformingInstructor from './TopPerformingInstructor/TopPerformingInstructor';
import { axiosInstance } from '../../../../utils/tokenUtils';
import AnalyticsSectionCard from './AnalyticsSectionCard';
import { CardSkeleton, DonutSkeleton } from '../../SkeletonLoader/SkeletonLoader';

const UserTeacherAnalytics = ({ timePeriod = 'last_month' }) => {
  const [activeMetricsData, setActiveMetricsData] = useState(null);
  const [instructorsLearnersData, setInstructorsLearnersData] = useState(null);
  const [churnRateData, setChurnRateData] = useState(null);
  const [retentionData, setRetentionData] = useState(null);
  const [supportTicketsData, setSupportTicketsData] = useState(null);
  const [userAnalyticsData, setUserAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);

  const getApiPeriod = useCallback(() => {
    switch (timePeriod) {
      case 'last_day':
        return 'today';
      case 'last_week':
        return '1_week';
      case 'last_2_weeks':
        return '2_weeks';
      case 'last_month':
        return '1_month';
      case 'last_3_months':
        return '3_months';
      case 'last_6_months':
        return '6_months';
      case 'last_year':
        return '1_year';
      default:
        return '1_month';
    }
  }, [timePeriod]);

  // Helper to get comparison period text
  const getComparisonPeriodText = () => {
    switch (timePeriod) {
      case 'last_day':
        return 'previous day';
      case 'last_week':
        return 'previous week';
      case 'last_2_weeks':
        return 'last 2 weeks';
      case 'last_month':
        return 'last month';
      case 'last_3_months':
        return 'last 3 months';
      case 'last_6_months':
        return 'last 6 months';
      case 'last_year':
        return 'previous year';
      default:
        return 'last month';
    }
  };

  useEffect(() => {
    let isMounted = true;
    async function fetchAllAnalytics() {
      setLoading(true);
      try {
        const apiPeriod = getApiPeriod();
        const [
          activeRes,
          instructorsLearnersRes,
          churnRes,
          retentionRes,
          supportRes,
          userAnalyticsRes,
        ] = await Promise.allSettled([
          axiosInstance.get(`/admin/dashboard/active-metrics?period=${apiPeriod}`),
          axiosInstance.get(`/admin/dashboard/instructors-learners-metrics?period=${apiPeriod}`),
          axiosInstance.get(`/admin/dashboard/churn-rate-metrics?period=${apiPeriod}`),
          axiosInstance.get(`/admin/dashboard/retention-metrics?period=${apiPeriod}`),
          axiosInstance.get(`/admin/dashboard/support-tickets-metrics?period=${apiPeriod}`),
          axiosInstance.get(`/admin/dashboard/user-analytics`),
        ]);

        // allSettled: a failed request only blanks its own section (shown as
        // "--"); the others still show their data.
        if (isMounted) {
          setActiveMetricsData(settledData(activeRes, 'active metrics'));
          setInstructorsLearnersData(
            settledData(instructorsLearnersRes, 'instructors & learners metrics')
          );
          setChurnRateData(settledData(churnRes, 'churn rate metrics'));
          setRetentionData(settledData(retentionRes, 'retention metrics'));
          setSupportTicketsData(settledData(supportRes, 'support tickets metrics'));
          setUserAnalyticsData(settledData(userAnalyticsRes, 'user analytics'));
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    fetchAllAnalytics();
    return () => {
      isMounted = false;
    };
  }, [timePeriod, getApiPeriod]);

  // Prepare data for cards from new APIs
  const totalTeachers = instructorsLearnersData?.total_instructors?.value || 0;
  const totalStudents = instructorsLearnersData?.total_learners?.value || 0;
  const activeUsers = activeMetricsData?.active_users?.value || 0;
  const activeSessions = activeMetricsData?.active_sessions?.value || 0;

  // Growth rates from new APIs
  const teacherGrowth = readGrowth(instructorsLearnersData?.total_instructors?.growth_percentage);
  const studentGrowth = readGrowth(instructorsLearnersData?.total_learners?.growth_percentage);
  const activeUsersGrowth = readGrowth(activeMetricsData?.active_users?.growth_percentage);
  const activeSessionsGrowth = readGrowth(activeMetricsData?.active_sessions?.growth_percentage);

  // Onboarding data
  const avgOnboardingTime = instructorsLearnersData?.avg_onboarding_time?.value || 0;
  // false when no instructor was approved in the period (nothing to average)
  const onboardingHasData = instructorsLearnersData?.avg_onboarding_time?.has_data !== false;
  const avgOnboardingTimeGrowth = readGrowth(
    instructorsLearnersData?.avg_onboarding_time?.growth_percentage
  );
  const totalScheduledInstructors =
    instructorsLearnersData?.total_scheduled_instructors?.value || 0;
  const totalScheduledInstructorsGrowth = readGrowth(
    instructorsLearnersData?.total_scheduled_instructors?.growth_percentage
  );

  // Churn rates from new API
  const studentChurnRate = churnRateData?.learner_churn_rate?.value || 0;
  // false when nobody was active before the period, so churn can't be measured
  const studentChurnHasData = churnRateData?.learner_churn_rate?.has_data !== false;
  const studentChurnRateGrowth = readGrowth(churnRateData?.learner_churn_rate?.growth_percentage);
  const teacherChurnRate = churnRateData?.instructor_churn_rate?.value || 0;
  const teacherChurnHasData = churnRateData?.instructor_churn_rate?.has_data !== false;
  const teacherChurnRateGrowth = readGrowth(
    churnRateData?.instructor_churn_rate?.growth_percentage
  );

  // Retention rates from new API
  const studentRetentionRate = retentionData?.learners_retention_rate?.value || 0;
  const studentRetentionRateGrowth = readGrowth(
    retentionData?.learners_retention_rate?.growth_percentage
  );
  const teacherRetentionRate = retentionData?.instructor_retention_rate?.value || 0;
  const teacherRetentionRateGrowth = readGrowth(
    retentionData?.instructor_retention_rate?.growth_percentage
  );

  // Support tickets from new API
  const openTickets = supportTicketsData?.open_tickets || 0;
  const resolvedTickets = supportTicketsData?.resolved_tickets || 0;
  const rejectedTickets = supportTicketsData?.rejected_tickets || 0;
  // Total comes from the API: open + resolved + rejected
  const totalTickets = supportTicketsData?.total_tickets || 0;
  const supportTicketsGrowth = readGrowth(supportTicketsData?.growth_percentage);

  // Donut chart data from new user analytics API
  const teacherData = [
    { label: 'Academic', value: userAnalyticsData?.teachers?.academic || 0 },
    { label: 'Creative', value: userAnalyticsData?.teachers?.creative || 0 },
  ];

  return (
    <Box className="user-teacher-analytics-section">
      <Typography className="user-teacher-analytics-title">User Analytics</Typography>
      <Grid container spacing={2} className="user-teacher-analytics-cards">
        <Grid container item spacing={2}>
          <Grid item container xs={12} md={7} direction="column" spacing={2}>
            <Grid item>
              <AnalyticsSectionCard title="Instructors & Learners">
                {loading ? (
                  <CardSkeleton />
                ) : (
                  <>
                    <AnalyticsCard
                      title="New Instructors"
                      value={totalTeachers}
                      growth={teacherGrowth}
                      unavailable={!instructorsLearnersData}
                      comparisonPeriod={getComparisonPeriodText()}
                    />
                    <AnalyticsCard
                      title="New Learners"
                      value={totalStudents}
                      growth={studentGrowth}
                      unavailable={!instructorsLearnersData}
                      comparisonPeriod={getComparisonPeriodText()}
                    />
                    <AnalyticsCard
                      title="Avg. Onboarding Time"
                      value={
                        onboardingHasData ? `${avgOnboardingTime.toFixed(2)} hrs` : 'No approvals'
                      }
                      growth={avgOnboardingTimeGrowth}
                      unavailable={!instructorsLearnersData}
                      higherIsBetter={false}
                      comparisonPeriod={getComparisonPeriodText()}
                    />
                    <AnalyticsCard
                      title="New Instructors Scheduled"
                      value={totalScheduledInstructors}
                      growth={totalScheduledInstructorsGrowth}
                      unavailable={!instructorsLearnersData}
                      comparisonPeriod={getComparisonPeriodText()}
                    />
                  </>
                )}
              </AnalyticsSectionCard>
            </Grid>

            {/* Retention & Cohort Section */}
            <Grid item>
              <AnalyticsSectionCard title="Retention & Cohort">
                {loading ? (
                  <CardSkeleton />
                ) : (
                  <>
                    <AnalyticsCard
                      title="Learners Retention Rate"
                      value={`${studentRetentionRate.toFixed(1)}%`}
                      growth={studentRetentionRateGrowth}
                      unavailable={!retentionData}
                      comparisonPeriod={getComparisonPeriodText()}
                    />
                    <AnalyticsCard
                      title="Instructors Retention Rate"
                      value={`${teacherRetentionRate.toFixed(1)}%`}
                      growth={teacherRetentionRateGrowth}
                      unavailable={!retentionData}
                      comparisonPeriod={getComparisonPeriodText()}
                    />
                  </>
                )}
              </AnalyticsSectionCard>
            </Grid>
          </Grid>

          {/* Right Column: Active + Churn Rate + Support */}
          <Grid item container xs={12} md={5} direction="column" spacing={2}>
            {/* Active Section */}
            <Grid item>
              <AnalyticsSectionCard title="Active">
                {loading ? (
                  <CardSkeleton />
                ) : (
                  <>
                    <AnalyticsCard
                      title="Active Users"
                      value={activeUsers}
                      growth={activeUsersGrowth}
                      unavailable={!activeMetricsData}
                      comparisonPeriod={getComparisonPeriodText()}
                    />
                    <AnalyticsCard
                      title="Active Sessions"
                      value={activeSessions}
                      growth={activeSessionsGrowth}
                      unavailable={!activeMetricsData}
                      comparisonPeriod={getComparisonPeriodText()}
                    />
                  </>
                )}
              </AnalyticsSectionCard>
            </Grid>

            {/* Churn Rate Section */}
            <Grid item>
              <AnalyticsSectionCard title="Churn Rate">
                {loading ? (
                  <CardSkeleton />
                ) : (
                  <>
                    <AnalyticsCard
                      title="Learner Churn Rate"
                      value={studentChurnHasData ? `${studentChurnRate.toFixed(1)}%` : 'No data'}
                      growth={studentChurnRateGrowth}
                      unavailable={!churnRateData}
                      higherIsBetter={false}
                      comparisonPeriod={getComparisonPeriodText()}
                    />
                    <AnalyticsCard
                      title="Instructor Churn Rate"
                      value={teacherChurnHasData ? `${teacherChurnRate.toFixed(1)}%` : 'No data'}
                      growth={teacherChurnRateGrowth}
                      unavailable={!churnRateData}
                      higherIsBetter={false}
                      comparisonPeriod={getComparisonPeriodText()}
                    />
                  </>
                )}
              </AnalyticsSectionCard>
            </Grid>

            {/* Support Tickets Section */}
            <Grid item>
              {loading ? (
                <CardSkeleton />
              ) : (
                <AnalyticsCard
                  title="Support Tickets"
                  value={`${totalTickets} Total · ${openTickets} Open / ${resolvedTickets} Resolved / ${rejectedTickets} Rejected`}
                  growth={supportTicketsGrowth}
                  unavailable={!supportTicketsData}
                  higherIsBetter={false}
                  comparisonPeriod={getComparisonPeriodText()}
                  actionButton={{
                    label: 'View',
                    route: '/admin/support',
                  }}
                />
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {/* Retention and Cohort Analysis */}
      <Grid container spacing={2} className="user-teacher-analytics-cards"></Grid>
      <Grid container spacing={2} className="user-teacher-analytics-middle-section">
        <Grid item xs={12} md={8}>
          <TopPerformingInstructor timePeriod={timePeriod} />
        </Grid>
        <Grid item xs={12} md={4}>
          {loading ? (
            <DonutSkeleton />
          ) : !userAnalyticsData ? (
            <SectionUnavailable title="Instructor" />
          ) : (
            <DonutChart
              title="Instructor"
              centerValue={userAnalyticsData?.teachers?.total || 0}
              data={teacherData}
              colors={['#2563EB', '#7BCB8F']}
              isUserAnalytics={true}
            />
          )}
        </Grid>
      </Grid>
      <Grid container spacing={2} className="user-teacher-analytics-charts">
        <Grid item xs={12}>
          <UserTeacherLineChart title="Registration & Availability Ratio Chart" />
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserTeacherAnalytics;
